import { CloseOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Divider,
  Flex,
  Form,
  FormProps,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Typography
} from "antd";
import { sendMessage } from "api/services/MessageService";
import { TWebDMessage } from "api/services/models/message.types";
import { getAllUsersList } from "api/services/UserService";
import { useTDAuthentication } from "auth/AuthenticationProvider";
import { AxiosError } from "axios";
import ErrorViewer from "components/ErrorViewer";
import { useLayoutContext } from "components/MainContainer";
import { ReactComponent as AddIcon } from "Icons/vuesax/add.svg";
import { ReactComponent as SendIcon } from "Icons/vuesax/send-2.svg";
import { ReactComponent as TickSquareLinear } from "Icons/vuesax/tick-square.svg";
import { useEffect, useState } from "react";

type TNewMessageProps = {
  onClose: (message?: TWebDMessage[]) => void;
};

const Header = ({ onSubmit, onClose }: { onSubmit: () => void; onClose: () => void }) => (
  <Flex
    wrap
    gap="middle"
    align="center"
    justify="space-between"
    style={{ alignItems: "baseline" }}>
    <Typography.Title level={4}>{"New Message"}</Typography.Title>
    <Space size="small">
      <Button
        type="primary"
        htmlType="submit"
        className="default-button primary-btn"
        onClick={onSubmit}>
        <TickSquareLinear className="default-icon" />
        <span>Send</span>
      </Button>
      <Button
        style={{ border: "none" }}
        icon={<CloseOutlined style={{ fontSize: ".75rem" }} />}
        onClick={e => onClose()}></Button>
    </Space>
  </Flex>
);

const NewMessage = ({ onClose }: TNewMessageProps) => {
  const { accountId, webDUser } = useTDAuthentication();
  const [users, setUsers] = useState<string[]>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<AxiosError | null>();
  const [form] = Form.useForm();
  const { toggleDrawer, isSmallScreen } = useLayoutContext();

  useEffect(() => {
    if (webDUser?.isInternalUser() && accountId) {
      setLoading(true);
      getAllUsersList(accountId)
        .then(users => setUsers(["All Users", ...users]))
        .catch(setError)
        .finally(() => {
          setLoading(false);
          form.setFieldValue("to", null);
        });
    } else {
      setUsers(["Customer Support", "Technical Support", "Other"]);
    }
  }, [accountId]);

  useEffect(() => {
    document.body.classList.toggle("no-scroll", !isSmallScreen);
  }, [isSmallScreen]);

  const onSave = () => {
    form
      .validateFields()
      .then(() => form.submit())
      .catch(setError);
  };

  const onFinish: FormProps<{ to: string; message: string }>["onFinish"] = values => {
    setLoading(true);
    setError(null);
    sendMessage({
      fromAccountId: webDUser?.user.accountId!,
      fromUserId: webDUser?.user.userId!,
      toAccountId: webDUser?.isInternalUser() ? accountId! : "TD",
      toUserId: values.to,
      messageText: values.message
    })
      .then(message => onClose(message))
      .catch(setError)
      .finally(() => setLoading(false));
  };

  return (
    <>
      {!isSmallScreen ? (
        <Modal
          open={true}
          width={800}
          maskClosable={false}
          centered={true}
          closeIcon={null}
          footer={null}
          onCancel={() => onClose()}
          title={<Header onSubmit={onSave} onClose={onClose} />}>
          <Card
            style={{
              marginTop: "16px",
              padding: 0
            }}
            className="default-border"
            styles={{ body: { padding: "16px" } }}>
            {loading && (
              <Flex justify="space-around" style={{ width: "100%", textAlign: "center" }}>
                <Spin tip="Saving, please wait..." />
              </Flex>
            )}
            {error && <ErrorViewer error={error} />}
            <Typography.Text>{`From: ${webDUser?.user.userId}@${webDUser?.user.accountId}`}</Typography.Text>
            <Divider />
            <Form
              form={form}
              labelAlign="right"
              name="new_message"
              onFinish={onFinish}
              layout="vertical"
              style={{ maxWidth: 800 }}
              validateTrigger={["onChange", "onBlur"]}>
              <Form.Item key={"to"} name={"to"} label={"To"} rules={[{ required: true }]}>
                <Select options={users?.map(u => ({ value: u, Label: u }))} />
              </Form.Item>

              <Form.Item
                key={"message"}
                name={"message"}
                label={"Message"}
                rules={[{ required: true, min: 3, max: 1024 }]}>
                <Input.TextArea
                  showCount
                  autoSize={{ minRows: 2, maxRows: 6 }}
                  maxLength={1024}
                />
              </Form.Item>
            </Form>
          </Card>
        </Modal>
      ) : (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "1rem" }}>
            <AddIcon
              className="close-icon"
              onClick={() => onClose()}
              width={"1.5rem"}
              height={"1.5rem"}
            />
            <Button
              style={{ marginLeft: "2rem" }}
              onClick={onSave}
              type="primary">
              <SendIcon width="1rem" height="1rem" />
              <span style={{ fontSize: ".875rem", fontWeight: "400" }}>Send</span>
            </Button>
          </div>
          <div style={{ padding: "1rem" }}>
            <Card
              className="card-style"
              style={{ width: "calc(100vw - 2rem)", height: "85vh" }}>
              {error && <ErrorViewer error={error} />}
              <Typography.Text className="font-1212" style={{ fontSize: "1rem" }}>
                Send a new message
              </Typography.Text>
              <Form
                form={form}
                labelAlign="right"
                name="new_message"
                onFinish={onFinish}
                layout="vertical"
                style={{ marginTop: "1.25rem" }}
                validateTrigger={["onChange", "onBlur"]}>
                <Form.Item key={"to"} name={"to"} label={"To"} rules={[{ required: true }]}>
                  <Select
                    options={users?.map(u => ({ value: u, Label: u }))}
                    variant="borderless"
                    style={{ width: "100%" }}
                    getPopupContainer={triggerNode => triggerNode.parentElement}
                    className="filter-input"
                  />
                </Form.Item>

                <Form.Item
                  key={"message"}
                  name={"message"}
                  label={"Message"}
                  rules={[{ required: true, min: 3, max: 1024 }]}>
                  <Input.TextArea
                    className="filter-input"
                    showCount
                    maxLength={1024}
                    style={{ minHeight: "55vh" }}
                  />
                </Form.Item>
              </Form>
            </Card>
          </div>
        </>
      )}
    </>
  );
};

export default NewMessage;
